// ─────────────────────────────────────────────────────────────────────────────
// Roles y Permisos de Pulso — modal de alta/edición (2026-08-08, petición de Adriana).
//
// Calcado de la pantalla Roles y Permisos de Brillamos (`views/recognition/admin/
// AdminRoleEditorModal.tsx`), pero con los permisos de PULSO (los 4 que vivían en la antigua
// pantalla de Administradores + los 2 de catálogo). Un solo formulario para alta y edición.
//
// Detección de "ya es administrador" (paridad con Brillamos): al elegir a alguien en el alta, si
// ya es AdminGlobal se bloquea (ese rol se gestiona en "Administradores"); si ya es administrador
// (de Pulso u otro módulo) se abre su edición real en lugar de un `createUser` que duplicaría.
//
// Diferencia con Brillamos: Pulso no tiene un endpoint único que combine rol+alcance+permisos, así
// que el guardado llama en SECUENCIA a los PATCH de UserAdminService (roles → alcance → permisos →
// estado). El orden importa: al cambiar los roles el backend limpia el alcance de quien queda sin
// rol acotado, así que el alcance se escribe DESPUÉS de los roles (igual que hacía UsersAdminView).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, UserSearchSelect, InitialsAvatar } from '@/components/common';
import type { UserSearchSelection } from '@/components/common';
import { ApiError } from '@/api/client';
import { OuTreeSelect } from '@/components/common/OuTree';
import type { AdminPermissions, OUItem } from '@/api/types';
import type { AdminUser } from '@/data/mockUsers';
import type { UserRole } from '@/hooks/useCurrentUser';
import { ROLE_LABEL_KEY } from '@/auth/roleLabels';
import {
  useCreateUser, useUpdateUserRoles, useUpdateUserOuScope, useUpdateUserPermissions, useToggleUserActive,
} from '@/hooks/useUsers';

/** Roles de Pulso administrables aquí. AdminGlobal se da de alta en "Administradores" (lateral). */
const ROLE_KEYS: UserRole[] = ['AdminPulso', 'AdminOU'];
/** El rol acotado a OU: al elegirlo aparece el árbol de OUs. */
const OU_SCOPED_ROLE: UserRole = 'AdminOU';
const GLOBAL_ROLE: UserRole = 'AdminGlobal';

/** Los 6 permisos de Pulso, en orden, con su clave i18n (namespace por defecto). */
const PERMISSIONS: Array<[keyof AdminPermissions, string]> = [
  ['canCreateSurveys',             'admin.permCreateSurveys'],
  ['canViewAnalytics',             'admin.permViewAnalytics'],
  ['canManageUsers',               'admin.permManageUsers'],
  ['canApproveSurveys',            'admin.permApproveSurveys'],
  ['canAccessCatalogFundamentals', 'admin.permCatalogFund'],
  ['canAccessCatalogOus',          'admin.permCatalogOus'],
];

const EMPTY_PERMS: AdminPermissions = {
  canCreateSurveys: false, canViewAnalytics: false, canManageUsers: false, canApproveSurveys: false,
  canAccessCatalogFundamentals: false, canAccessCatalogOus: false,
};

const TITLE_ID = 'roles-pulso-editor-title';

export type PulsoEditorTarget = { mode: 'create' } | { mode: 'edit'; admin: AdminUser };

interface PulsoDraft {
  role: UserRole | null;
  scopeOuIds: string[];
  isAllOus: boolean;
  active: boolean;
  perms: AdminPermissions;
}

interface Props {
  target: PulsoEditorTarget | null;
  /** Lista completa de admins (sin el filtro de búsqueda) — para detectar en el alta si la persona
   *  elegida ya es administrador (de Pulso u otro módulo) o AdminGlobal. */
  existingAdmins: AdminUser[];
  ouItems: OUItem[];
  onClose: () => void;
  onSaved: () => void;
}

function draftFromAdmin(admin: AdminUser): PulsoDraft {
  return {
    role: admin.roles.find(role => ROLE_KEYS.includes(role)) ?? null,
    scopeOuIds: [...admin.ouScopeIds],
    isAllOus: admin.isAllOusScope,
    active: admin.active,
    perms: {
      canCreateSurveys: !!admin.canCreateSurveys,
      canViewAnalytics: !!admin.canViewAnalytics,
      canManageUsers: !!admin.canManageUsers,
      canApproveSurveys: !!admin.canApproveSurveys,
      canAccessCatalogFundamentals: !!admin.canAccessCatalogFundamentals,
      canAccessCatalogOus: !!admin.canAccessCatalogOus,
    },
  };
}

export function RolesPulsoEditorModal({ target, existingAdmins, ouItems, onClose, onSaved }: Readonly<Props>) {
  const { t } = useTranslation();
  const createUser = useCreateUser();
  const updateRoles = useUpdateUserRoles();
  const updateOuScope = useUpdateUserOuScope();
  const updatePermissions = useUpdateUserPermissions();
  const toggleActive = useToggleUserActive();

  const [selectedUser, setSelectedUser] = useState<UserSearchSelection | null>(null);
  /** En el alta: el admin ya existente al que corresponde la persona elegida (edición real en vez
   *  de createUser). null = persona nueva. */
  const [existingMatch, setExistingMatch] = useState<AdminUser | null>(null);
  /** En el alta: se eligió a alguien que ya es AdminGlobal — no se gestiona desde aquí. */
  const [blockedGlobal, setBlockedGlobal] = useState(false);
  const [draft, setDraft] = useState<PulsoDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [errorForbidden, setErrorForbidden] = useState(false);

  useEffect(() => {
    setError(false);
    setErrorForbidden(false);
    setSaving(false);
    setBlockedGlobal(false);
    if (!target) { setSelectedUser(null); setDraft(null); setExistingMatch(null); return; }
    if (target.mode === 'edit') {
      setSelectedUser({ id: target.admin.id, email: target.admin.email ?? '', displayName: target.admin.name });
      setExistingMatch(target.admin);
      setDraft(draftFromAdmin(target.admin));
    } else {
      setSelectedUser(null);
      setExistingMatch(null);
      setDraft(null);
    }
  }, [target]);

  function handlePersonSelect(selection: UserSearchSelection | null) {
    setSelectedUser(selection);
    setBlockedGlobal(false);
    setError(false);
    if (!selection) { setDraft(null); setExistingMatch(null); return; }

    const already = existingAdmins.find(admin => admin.id === selection.id);
    if (already?.roles.includes(GLOBAL_ROLE)) {
      setBlockedGlobal(true);
      setDraft(null);
      setExistingMatch(null);
      return;
    }
    if (already) {
      // Ya es administrador (de Pulso u otro módulo): se abre su edición real, preservando el resto.
      setExistingMatch(already);
      setDraft(draftFromAdmin(already));
    } else {
      setExistingMatch(null);
      setDraft({ role: null, scopeOuIds: [], isAllOus: false, active: true, perms: { ...EMPTY_PERMS } });
    }
  }

  const isCreating = target?.mode === 'create';
  /** Se está editando a un admin ya existente (edición directa, o alta sobre alguien que ya lo era). */
  const baseAdmin = target?.mode === 'edit' ? target.admin : existingMatch;
  const isOuRole = draft?.role === OU_SCOPED_ROLE;
  const needsOu = isOuRole && !draft?.isAllOus && (draft?.scopeOuIds.length ?? 0) === 0;
  const canSave = draft !== null && draft.role !== null && !blockedGlobal
    && (!isCreating || selectedUser !== null) && !needsOu;

  const patchDraft = (patch: Partial<PulsoDraft>) => setDraft(d => (d ? { ...d, ...patch } : d));
  const applyRole = (role: UserRole) =>
    patchDraft({ role, scopeOuIds: role === OU_SCOPED_ROLE ? draft?.scopeOuIds ?? [] : [], isAllOus: role === OU_SCOPED_ROLE ? draft?.isAllOus ?? false : false });
  const togglePerm = (key: keyof AdminPermissions) =>
    setDraft(d => (d ? { ...d, perms: { ...d.perms, [key]: !d.perms[key] } } : d));

  async function save() {
    if (!draft || !draft.role || !canSave) return;
    setSaving(true);
    setError(false);
    setErrorForbidden(false);
    try {
      let userId: string | undefined;
      if (baseAdmin) {
        // Edición (directa o sobre un admin ya existente): preservar los roles que no son de Pulso.
        userId = baseAdmin.id;
        const existingOtherRoles = baseAdmin.roles.filter(role => !ROLE_KEYS.includes(role));
        await updateRoles.mutateAsync({ id: userId, roles: [draft.role, ...existingOtherRoles] });
      } else if (selectedUser) {
        const created = await createUser.mutateAsync({
          aadObjectId: selectedUser.id,
          email: selectedUser.email ?? '',
          displayName: selectedUser.displayName,
          roles: [draft.role],
        });
        userId = created?.id;
      }
      if (!userId) throw new Error('no user id');

      if (isOuRole) {
        await updateOuScope.mutateAsync({
          id: userId,
          ouIds: draft.isAllOus ? [] : draft.scopeOuIds,
          allOusAccess: draft.isAllOus,
        });
      }
      await updatePermissions.mutateAsync({ id: userId, perms: draft.perms });
      if (baseAdmin && baseAdmin.active !== draft.active) {
        await toggleActive.mutateAsync({ id: userId, isActive: draft.active });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(true);
      setErrorForbidden(e instanceof ApiError && e.status === 403);
    } finally {
      setSaving(false);
    }
  }

  const titleText = target?.mode === 'edit'
    ? t('admin.editAdmin')
    : t('admin.newAdmin');

  return (
    <Modal open={target !== null} onClose={onClose} maxWidth={640} labelledBy={TITLE_ID}>
      <ModalHeader onClose={onClose}><h2 id={TITLE_ID}>{titleText}</h2></ModalHeader>
      <ModalBody>
        {isCreating && (
          <div className="form-group">
            <label htmlFor="roles-pulso-search">{t('admin.adminUser')} *</label>
            <UserSearchSelect
              inputId="roles-pulso-search"
              value={selectedUser}
              onSelect={handlePersonSelect}
              placeholder={t('admin.userSearchPlaceholder')}
            />
            {blockedGlobal && (
              <p className="rp-error" role="alert">{t('admin.rolesPulsoCannotEditGlobal')}</p>
            )}
          </div>
        )}

        {!isCreating && selectedUser && (
          <div className="adm-user-cell" style={{ marginBottom: '1rem' }}>
            <InitialsAvatar
              name={selectedUser.displayName || selectedUser.email || '?'}
              baseClass="adm-user-avatar"
              seed={selectedUser.id}
            />
            <div>
              <div className="adm-user-name">{selectedUser.displayName}</div>
              <div className="adm-text-muted adm-user-email">{selectedUser.email}</div>
            </div>
          </div>
        )}

        {draft && (
          <>
            {isCreating && existingMatch && (
              <p className="adm-role-note">{t('admin.rolesPulsoAlreadyNote')}</p>
            )}
            <div className="form-group">
              <label htmlFor="roles-pulso-role">{t('admin.roles')} *</label>
              <select
                id="roles-pulso-role"
                className="form-input"
                value={draft.role ?? ''}
                onChange={e => applyRole(e.target.value as UserRole)}
              >
                {draft.role === null && <option value="" disabled>{t('admin.adminRolePlaceholder')}</option>}
                {ROLE_KEYS.map(role => (
                  <option key={role} value={role}>{t(ROLE_LABEL_KEY[role])}</option>
                ))}
              </select>
            </div>

            {isOuRole && (
              <div className="form-group">
                <label>{t('admin.adminOU')} *</label>
                <button
                  type="button"
                  className={`ou-scope-all-btn${draft.isAllOus ? ' active' : ''}`}
                  onClick={() => patchDraft({ isAllOus: !draft.isAllOus, scopeOuIds: draft.isAllOus ? draft.scopeOuIds : [] })}
                >
                  {t('admin.adminOUAll')}
                </button>
                <OuTreeSelect
                  mode="multi"
                  variant="dropdown"
                  items={ouItems}
                  value={draft.scopeOuIds}
                  onChange={ouScopeIds => patchDraft({ scopeOuIds: ouScopeIds })}
                  placeholder={t('admin.adminOUPlaceholder')}
                  isDisabled={draft.isAllOus}
                />
              </div>
            )}

            <div className="form-group">
              <label className="modules-option">
                <input type="checkbox" checked={draft.active} onChange={() => patchDraft({ active: !draft.active })} />
                {t('admin.colEstado')}
              </label>
            </div>

            <div className="form-group">
              <label>{t('admin.colPermisos')}</label>
              <div className="perm-toggles-list">
                {PERMISSIONS.map(([key, labelKey]) => (
                  <label key={key} className="modules-option">
                    <input type="checkbox" checked={!!draft.perms[key]} onChange={() => togglePerm(key)} />
                    {t(labelKey)}
                  </label>
                ))}
              </div>
            </div>

            {error && <p className="rp-error" role="alert">{t(errorForbidden ? 'admin.userSaveForbidden' : 'admin.userSaveError')}</p>}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn btn--primary" onClick={() => void save()} disabled={!canSave || saving}>
          {saving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}{' '}{t('common.save')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default RolesPulsoEditorModal;
